import { useEffect, useState } from "react";
import type { ApiThreadComment, ThreadContext } from "lib/threadCommentUtils";
import { trpc } from "lib/trpc";
import type { ClientCurrentUser } from "@compass/shared/types";
import { UnifiedThreadComment } from "./UnifiedThreadComment";

export interface CommentRepliesProps {
    comment: ApiThreadComment;
    context: ThreadContext;
    threadId: number;
    currentUser?: ClientCurrentUser;
    depth?: number;
    onReply?: (parentComment: ApiThreadComment) => void;
    onEdit?: (comment: ApiThreadComment) => void;
    onDelete?: (commentId: number) => void;
    shareUrlBuilder?: (commentId: number) => string | undefined;
    /** When true, auto-expands the replies (e.g. after creating a reply) */
    forceExpanded?: boolean;
}

/**
 * Renders nested replies for a comment.
 * Shows a "View N replies" link when children exist, lazily loads them on click.
 * Recursively renders deeper nesting levels.
 */
export function CommentReplies({
    comment,
    context,
    threadId,
    currentUser,
    depth = 0,
    onReply,
    onEdit,
    onDelete,
    shareUrlBuilder,
    forceExpanded,
}: CommentRepliesProps) {
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (forceExpanded) setExpanded(true);
    }, [forceExpanded]);

    const { data: childComments = [], isLoading } = trpc.threads.getChildComments.useQuery(
        { parentCommentId: comment.id },
        { enabled: expanded },
    );

    if (comment.childCommentCount === 0 && !expanded) {
        return null;
    }

    return (
        <div className="ml-8 mt-2 border-l-2 border-gray-100 pl-4">
            {!expanded && comment.childCommentCount > 0 && (
                <button
                    type="button"
                    onClick={() => setExpanded(true)}
                    className="cursor-pointer text-xs font-medium text-blue-600 hover:text-blue-800"
                >
                    View {comment.childCommentCount}{" "}
                    {comment.childCommentCount === 1 ? "reply" : "replies"}
                </button>
            )}

            {expanded && isLoading && <p className="text-xs text-gray-400">Loading replies...</p>}

            {expanded &&
                childComments.map((child) => (
                    <div key={child.id} className="mb-3">
                        <UnifiedThreadComment
                            comment={child}
                            context={context}
                            currentUser={currentUser}
                            onEdit={onEdit ? () => onEdit(child) : undefined}
                            onDelete={onDelete ? () => onDelete(child.id) : undefined}
                            onReply={onReply ? () => onReply(child) : undefined}
                            shareUrl={shareUrlBuilder?.(child.id)}
                            repliesSlot={
                                <CommentReplies
                                    comment={child}
                                    context={context}
                                    threadId={threadId}
                                    currentUser={currentUser}
                                    depth={depth + 1}
                                    onReply={onReply}
                                    onEdit={onEdit}
                                    onDelete={onDelete}
                                    shareUrlBuilder={shareUrlBuilder}
                                />
                            }
                        />
                    </div>
                ))}

            {expanded && !isLoading && childComments.length === 0 && (
                <p className="text-xs text-gray-400">No replies yet</p>
            )}

            {expanded && !isLoading && (
                <button
                    type="button"
                    onClick={() => setExpanded(false)}
                    className="cursor-pointer text-xs text-gray-400 hover:text-gray-600"
                >
                    Hide replies
                </button>
            )}
        </div>
    );
}
